import LOCAL_STORAGE_KEY from '@/shared/constants/localStorageKey';
import { getStorage } from '@/shared/utils/general.util';
/**
 * Helpers para leer los items de una compra, venga de donde venga. Hoy hay
 * TRES formas del mismo hecho y estas funciones son el único lugar que conoce
 * las diferencias:
 *
 *   1. `lastOrderPurchase` de localStorage y respuestas de `processv2`, que
 *      mezclan snake_case y camelCase según el path:
 *        shopping_item / shoppingItem · product_version / productVersion
 *   2. `CartDetail` de GET /shopping-carts/{id} (sales-transaction-engine):
 *      `items[]` planos —el producto ya viene resuelto en el item— y los
 *      montos con nombres semánticos (`paid` en vez de `total`).
 *
 * Todo lo que consume una compra (el mapeo a PurchasedProduct, la puerta del
 * tour, el desglose de heru-cash) pasa por aquí, así que agregar una forma es
 * tocar este archivo y nada más.
 */

import { isRegularizationProduct } from '../../shared/utils/ecommerce.util';
import {
	isPackageLine,
	isPackagePlanLine,
} from '../../features/regularization/offer/packageCart';
import { getMonthName } from '../../shared/utils/dates.util';

export const getOrderItems = (order: any): any[] =>
	order?.shopping_item ?? order?.shoppingItem ?? order?.items ?? [];

export const getItemProductVersion = (item: any): any =>
	item?.product_version ?? item?.productVersion;

// En CartDetail el producto vive plano en el item; se sintetiza el mismo objeto
// que devuelven las otras formas para que los consumidores no bifurquen.
export const getItemProduct = (item: any): any =>
	getItemProductVersion(item)?.product ??
	item?.product ?? (item?.slug || item?.name
		? { slug: item.slug, name: item.name, category: item.category }
		: undefined);

export const getItemSlug = (item: any): string =>
	getItemProduct(item)?.slug ?? '';

export const getItemProductVersionId = (item: any): number | undefined =>
	getItemProductVersion(item)?.id ?? item?.product_version_id;

/** Precio de lista de una línea, antes de cualquier descuento. */
export const getItemListCents = (item: any): number => {
	const list = item?.base_price ?? item?.list;
	return typeof list === 'number' ? list : 0;
};

/** Descuento aplicado a una línea: lo que la alianza o el cupón le ahorraron. */
export const getItemDiscountCents = (item: any): number => {
	const discount = item?.discount;
	return typeof discount === 'number' ? discount : 0;
};

/**
 * Cada cuánto se cobra la línea, normalizado. Las formas legacy traen
 * `periodicity` como número de días (30/365) o como texto ('monthly'/'annual');
 * CartDetail trae `periodicity_days`. Todas convergen aquí.
 */
export const getItemPeriodicity = (item: any): 'monthly' | 'annual' | null => {
	const raw =
		item?.periodicity_days ??
		getItemProductVersion(item)?.periodicity ??
		item?.periodicity;
	if (raw === 'annual' || raw === 365) return 'annual';
	if (raw === 'monthly' || raw === 30) return 'monthly';
	return null;
};

/** Lo cobrado de una línea. `total` en las formas legacy, `paid` en CartDetail. */
export const getItemTotalCents = (item: any): number => {
	const total = item?.total ?? item?.paid;
	return typeof total === 'number' ? total : 0;
};

/** Lo que realmente se cobró por la compra completa, ya neto de heru-cash. */
export const getOrderPaidCents = (order: any): number =>
	Number(order?.totals?.paid ?? order?.total ?? 0);

/**
 * Cuándo se compró. De aquí sale QUÉ mes cubre la suscripción (regla del día
 * 15), así que reabrir una compra vieja muestra su mes y no el de hoy. Si el
 * dato no viene, se cae a hoy: es lo correcto para una compra recién hecha, que
 * es el único caso donde puede faltar.
 */
export const getOrderCreatedAt = (order: any): Date => {
	const raw = order?.created_at ?? order?.createdAt;
	if (!raw) return new Date();
	const parsed = new Date(raw);
	return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

/** heru-cash aplicado a la compra. Es de carrito, no de item. */
export const getOrderCreditsCents = (order: any): number =>
	Number(order?.totals?.credits ?? order?.credits_discount ?? 0);

/**
 * El slug sintético del paquete «Ponte al día».
 *
 * No existe en el catálogo a propósito: el paquete NO es un producto, son las
 * mismas líneas de regularización de siempre con un sello en
 * `additional_information` (ver packageCart). Este slug solo vive el
 * tiempo de pintar el recibo, para que resolvePurchasedProductDisplay le pueda
 * dar nombre.
 */
export const PACKAGE_GROUP_SLUG = 'ponte_al_dia_paquete';

/**
 * Un periodo comprado, listo para pintar.
 *
 * Lleva su `kind` porque en la pantalla las mensuales y las anuales NO son lo
 * mismo y se separan: quince chips iguales en fila hacen que la anual se lea
 * como un mes más, que es justo lo que pasaba.
 */
export type PeriodChip = { label: string; kind: 'month' | 'annual' };

export type GroupedOrderItem = {
	groupKey: string | number;
	slug: string;
	count: number;
	totalCents: number;
	/** Suma de precios de lista del grupo, antes de descuentos. */
	listCents: number;
	/** Suma de descuentos del grupo: el ahorro atribuible a ESTE producto. */
	discountCents: number;
	periods: PeriodChip[];
	items: any[];
};

const formatPeriodLabel = (item: any): PeriodChip | null => {
	const info = item?.additional_information;
	const month = info?.month;
	const year = info?.year;
	if (!year) return null;
	// La anual se nombra entera. El payload le pone month:1 para que el backend
	// tenga un periodo, así que sin esto salía "Ene 2024" al lado de quince
	// meses reales: el usuario contaba dieciséis mensuales y ninguna anual.
	if (info?.package_line === 'annual') {
		return { label: `Declaración anual ${year}`, kind: 'annual' };
	}
	if (!month) return null;
	const monthName = getMonthName(Number(month), true).slice(0, 3);
	return { label: `${monthName} ${year}`, kind: 'month' };
};

export const groupOrderItems = (order: any): GroupedOrderItem[] => {
	const items = getOrderItems(order);
	const groups = new Map<string | number, GroupedOrderItem>();

	items.forEach((item: any) => {
		const info = item?.additional_information;
		// El paquete se compró como UNA cosa y así se enseña. Sus líneas van al
		// mismo grupo aunque tengan product_version distintos —los meses y la
		// anual lo tienen— porque el usuario aceptó un precio por el conjunto,
		// no una lista de trámites: partirlo en tarjetas le pide reconstruir a
		// mano la suma que la oferta ya le había dado hecha.
		//
		// El plan del brazo `paquete_plan` queda FUERA, con su propia tarjeta.
		// Es la otra mitad de lo que compró, y esconderlo dentro del desglose
		// borraría medio ofrecimiento. Mismo criterio que ya usa el carrito.
		const isPackage = isPackageLine(info) && !isPackagePlanLine(info);
		const slug = isPackage ? PACKAGE_GROUP_SLUG : getItemSlug(item);
		const key = isPackage
			? PACKAGE_GROUP_SLUG
			: getItemProductVersionId(item) ?? slug ?? item?.id ?? Math.random();
		const total = getItemTotalCents(item);

		const discount = getItemDiscountCents(item);
		const list = getItemListCents(item);

		const existing = groups.get(key);
		if (existing) {
			existing.count += 1;
			existing.totalCents += total;
			existing.listCents += list;
			existing.discountCents += discount;
			existing.items.push(item);
		} else {
			groups.set(key, {
				groupKey: key,
				slug,
				count: 1,
				totalCents: total,
				listCents: list,
				discountCents: discount,
				periods: [],
				items: [item],
			});
		}
	});

	return Array.from(groups.values()).map((group) => {
		const product = getItemProduct(group.items[0]);
		// El paquete lista TODOS sus periodos, vengan de meses o de anuales: es
		// justo lo que el usuario quiere verificar de un vistazo.
		if (group.slug === PACKAGE_GROUP_SLUG) {
			const periods = group.items
				.map(formatPeriodLabel)
				.filter((chip): chip is PeriodChip => Boolean(chip));
			return { ...group, periods };
		}
		if (isRegularizationProduct(product?.category ?? '', group.slug)) {
			const periods = group.items
				.map(formatPeriodLabel)
				.filter((chip): chip is PeriodChip => Boolean(chip));
			return { ...group, periods };
		}
		return group;
	});
};

const parseStored = (key: string): any => {
	try {
		return JSON.parse(getStorage().getItem(key) || '{}');
	} catch {
		return {};
	}
};

/**
 * La compra recién hecha, tal como la dejaron StripeWidget y ModalPaypal al
 * confirmar. Es la fuente cuando NO hay `cartId`: el flujo post-checkout no
 * puede pedirla a la API sin marcar la compra como una reentrada.
 *
 * ÚNICO lector de storage para esto. Antes lo parseaban por su cuenta también
 * `SuccessfulPurchaseStandardContent` y `PurchaseTour`, así que una tercera
 * fuente (el preview de diseño) habría que haberla enchufado en tres lados.
 *
 * PREVIEW: con `VITE_PURCHASE_PREVIEW=true` gana una compra inventada escrita
 * en otra llave. La compra real NUNCA se pisa, así que apagar la env devuelve
 * el comportamiento de producción aunque el fixture siga en storage. En el
 * build de prod `import.meta.env` se inlinea y la rama desaparece.
 */
export const readLastOrderPurchase = (): any => {
	if (import.meta.env.VITE_PURCHASE_PREVIEW === 'true') {
		const preview = parseStored(LOCAL_STORAGE_KEY.PREVIEW_ORDER_PURCHASE);
		if (getOrderItems(preview).length > 0) return preview;
	}
	return parseStored(LOCAL_STORAGE_KEY.LAST_ORDER_PURCHASE);
};
